const { mainConfigService, walletConfigService, applePayConfigService, googlePayConfigService, samsungPayConfigService } = require('./config');
const { walletReserveFetch, walletCancelFetch } = require('../http');
const { elements, SDK_FOPS } = require('../variables');
const { sanitizeApiData } = require('../security');

const getWalletCurrency = (walletObj) =>
  walletObj?.currency_code ||
  walletObj?.billing?.available_balance?.currency_code ||
  null;

class WalletService {
  constructor(documentCheckout, callbackService, callbacks) {
    this.documentCheckout = documentCheckout;
    this.callbackService = callbackService;
    this.callbacks = callbacks;
    this._toggleInProgress = false;
    this.state = {
      active: null,
      walletBillingMap: {},
      _originalAmounts: {},
    };
  }

  // --- Public API ---

  setWalletBillingMap(paymentMethods = [], paymentServices = [], flexMethods = []) {
    const map = {};
    const allMethods = [...paymentMethods, ...paymentServices, ...flexMethods];

    allMethods.forEach((method) => {
      if (method.wallet_billing && method.code) {
        map[method.code] = method.wallet_billing;
      }
    });

    this.state.walletBillingMap = map;
  }

  restoreReservedState() {
    const reserved = walletConfigService.getReservedWallets();
    if (!reserved.length) return false;

    const active = reserved[0];
    const currencyCode = getWalletCurrency(active);
    if (!active.code || !currencyCode) return false;

    this.state.active = { code: active.code, currencyCode };
    walletConfigService.setActive(active.code, currencyCode);
    this.syncPayConfigAmounts();

    const walletWrapper = this._findWrapper(active.code, currencyCode);
    if (walletWrapper) {
      const toggle = walletWrapper.querySelector(`.${elements.walletToggle}`);
      if (toggle) toggle.checked = true;
      walletWrapper.classList.add('ottu__sdk-wallet-active');
      this._updateWalletDetails(walletWrapper);
    }

    this._setOtherWalletsDisabled(this.state.active, true);
    this.callbacks.filterMethodsByCurrency(currencyCode);

    return true;
  }

  getWalletBilling(methodCode) {
    if (!this.isActive()) return null;
    const walletBilling = this.state.walletBillingMap[methodCode];
    if (!walletBilling) return null;

    return walletBilling[this.state.active.code] || null;
  }

  getWalletServiceBilling() {
    const active = walletConfigService.getActiveWallet();
    return active ? active.billing || null : null;
  }

  async handleToggle(toggleElement) {
    if (this._toggleInProgress) return;

    const walletEl = toggleElement.closest(`.${elements.walletWrapper}`);
    const code = walletEl ? walletEl.dataset.walletCode : null;
    const currencyCode = walletEl ? walletEl.dataset.currency_code : null;
    const isChecked = toggleElement.checked;

    // Mutual-exclusion guard: another wallet is active → refuse toggle-on
    const active = this.state.active;
    if (
      isChecked && active &&
      (active.code !== code || active.currencyCode !== currencyCode)
    ) {
      toggleElement.checked = false;
      return;
    }

    if (!code || !currencyCode) return;

    this._toggleInProgress = true;
    toggleElement.disabled = true;
    walletEl.classList.add('ottu__sdk-wallet-loading');
    this.callbacks.setPayButtonDisabled(true);

    try {
      if (isChecked) {
        await this._activateWallet(walletEl, toggleElement, code, currencyCode);
      } else {
        await this._deactivateWallet(walletEl, toggleElement, code, currencyCode);
      }
    } finally {
      toggleElement.disabled = false;
      walletEl.classList.remove('ottu__sdk-wallet-loading');
      this._finalisePayButtonState();
      this._toggleInProgress = false;
    }
  }

  isActive() {
    return !!this.state.active;
  }

  getActiveCode() {
    return this.state.active ? this.state.active.code : null;
  }

  getActiveCurrencyCode() {
    return this.state.active ? this.state.active.currencyCode : null;
  }

  isFullCoverage() {
    const active = walletConfigService.getActiveWallet();
    return !!(this.state.active && active && active.submit_url);
  }

  getSubmitUrl() {
    const active = walletConfigService.getActiveWallet();
    return (active && active.submit_url) || '';
  }

  updateWalletDetailsForMethod(methodCode) {
    if (!this.isActive()) return;

    const billing = this.state.walletBillingMap[methodCode] || this.getWalletServiceBilling();
    if (!billing) return;

    const walletWrapper = this._findWrapper(this.state.active.code, this.state.active.currencyCode);
    if (!walletWrapper) return;

    this._updateWalletDetails(walletWrapper, billing);
  }

  syncPayConfigAmounts() {
    const active = this.isActive();
    const configMap = [
      { service: applePayConfigService, amountKey: 'amount', codeKey: 'gateway_code', stateKey: 'applePay' },
      { service: googlePayConfigService, amountKey: 'total_price', codeKey: 'code', stateKey: 'googlePay' },
      { service: samsungPayConfigService, amountKey: 'total_price', codeKey: 'code', stateKey: 'samsungPay' },
    ];

    for (const { service, amountKey, codeKey, stateKey } of configMap) {
      if (!service.isInitialized()) continue;
      const code = service.get(codeKey);
      if (!code) continue;

      if (active) {
        if (this.state._originalAmounts[stateKey] == null) {
          this.state._originalAmounts[stateKey] = service.get(amountKey);
        }
        const walletBilling = this.state.walletBillingMap[code];
        if (walletBilling?.remainder?.value) {
          service.updateConfig({ [amountKey]: walletBilling.remainder.value });
        }
      } else {
        if (this.state._originalAmounts[stateKey] != null) {
          service.updateConfig({ [amountKey]: this.state._originalAmounts[stateKey] });
          delete this.state._originalAmounts[stateKey];
        }
      }
    }
  }

  destroy() {
    this.state = {
      active: null,
      walletBillingMap: {},
      _originalAmounts: {},
    };
    walletConfigService.setActive(null, null);
    this._toggleInProgress = false;
  }

  // --- Private: Payload Processing ---

  _applyPayloadWalletData(payload) {
    if (!payload) return;

    mainConfigService.applyPayload(payload);

    // Upsert every wallet entry so non-active wallets stay fresh too
    if (payload.payment_services) {
      payload.payment_services
        .filter(s => s.flow === SDK_FOPS.WALLET)
        .forEach(w => walletConfigService.upsertWallet(w));
    }

    // Re-populate wallet billing map from updated methods/services
    const sanitizedMethods = (payload.payment_methods || []).map(sanitizeApiData);
    const sanitizedServices = (payload.payment_services || []).map(sanitizeApiData);
    const sanitizedFlex = (payload.flex_methods || []).map(sanitizeApiData);
    this.setWalletBillingMap(sanitizedMethods, sanitizedServices, sanitizedFlex);
  }

  // --- Private: Activation / Deactivation ---

  async _activateWallet(walletEl, toggleElement, code, currencyCode) {
    const wallet = walletConfigService.getWallet(code, currencyCode);
    if (!wallet || !wallet.reserve_url) {
      toggleElement.checked = false;
      return;
    }

    const apiKey = mainConfigService.get('apiKey');
    const sessionId = mainConfigService.get('session_id');

    try {
      const response = await walletReserveFetch(wallet.reserve_url, apiKey, code, sessionId, currencyCode);
      this._applyPayloadWalletData(response.sdk_setup_preload_payload);

      this.state.active = { code, currencyCode };
      walletConfigService.setActive(code, currencyCode);
      this.syncPayConfigAmounts();
      walletEl.classList.add('ottu__sdk-wallet-active');
      this._updateWalletDetails(walletEl);
      this._refreshSelectedMethodDisplay();
      this._setOtherWalletsDisabled(this.state.active, true);
      this.callbacks.filterMethodsByCurrency(currencyCode);
      this.callbacks.onFullCoverageChange(this.isFullCoverage());
    } catch (err) {
      const error = err instanceof Promise ? await err : err;
      const message = error?.detail || error?.message || 'Something went wrong';
      toggleElement.checked = false;
      this.callbacks.showErrorPopup(message);
    }
  }

  async _deactivateWallet(walletEl, toggleElement, code, currencyCode) {
    const wallet = walletConfigService.getWallet(code, currencyCode);
    const cancelUrl = wallet && wallet.cancel_url;
    if (!cancelUrl) {
      toggleElement.checked = true;
      return;
    }

    const apiKey = mainConfigService.get('apiKey');
    const sessionId = mainConfigService.get('session_id');

    try {
      const response = await walletCancelFetch(cancelUrl, apiKey, code, sessionId);
      this._applyPayloadWalletData(response.sdk_setup_preload_payload);

      this.state.active = null;
      walletConfigService.setActive(null, null);
      this.syncPayConfigAmounts();
      walletEl.classList.remove('ottu__sdk-wallet-active');
      this._refreshSelectedMethodDisplay();
      this._setOtherWalletsDisabled(null, false);
      this.callbacks.onFullCoverageChange(false);
      this.callbacks.filterMethodsByCurrency(null);
    } catch (err) {
      const error = err instanceof Promise ? await err : err;
      const message = error?.detail || error?.message || 'Something went wrong';
      toggleElement.checked = true;
      this.callbacks.showErrorPopup(message);
    }
  }

  // --- UI Updates ---

  _findWrapper(code, currencyCode) {
    if (!code || !currencyCode) return null;
    const esc = (typeof CSS !== 'undefined' && CSS.escape) ? CSS.escape.bind(CSS) : (v) => v;
    return this.documentCheckout.querySelector(
      `.${elements.walletWrapper}[data-wallet-code="${esc(code)}"][data-currency_code="${esc(currencyCode)}"]`
    );
  }

  _setOtherWalletsDisabled(active, disabled) {
    const wrappers = this.documentCheckout.querySelectorAll(`.${elements.walletWrapper}`);
    wrappers.forEach(wrapper => {
      const walletCode = wrapper.dataset.walletCode;
      const walletCurrency = wrapper.dataset.currency_code;
      const isActiveOne = !!(
        active &&
        walletCode === active.code &&
        walletCurrency === active.currencyCode
      );

      if (disabled && !isActiveOne) {
        wrapper.classList.add(elements.walletWrapperDisabled);
        wrapper.setAttribute('aria-disabled', 'true');
        const toggle = wrapper.querySelector(`.${elements.walletToggle}`);
        if (toggle) toggle.disabled = true;
      } else if (!disabled || isActiveOne) {
        wrapper.classList.remove(elements.walletWrapperDisabled);
        wrapper.removeAttribute('aria-disabled');
        const toggle = wrapper.querySelector(`.${elements.walletToggle}`);
        if (toggle) toggle.disabled = false;
      }
    });
  }

  _updateWalletDetails(walletWrapper, billing = null) {
    billing = billing || this.getWalletServiceBilling();
    if (!billing) return;

    const headerEl = walletWrapper.querySelector('.ottu__sdk-wallet-balance-amount');
    const headerSource = billing.balance_before || billing.available_balance;
    if (headerEl && headerSource && headerSource.display) {
      headerEl.textContent = headerSource.display;
    }

    const balanceChangeEl = walletWrapper.querySelector('.ottu__sdk-wallet-balance-change');
    if (balanceChangeEl && billing.balance_before && billing.balance_after) {
      balanceChangeEl.textContent =
        `${billing.balance_before.display} → ${billing.balance_after.display}`;
    }

    const appliedValueEl = walletWrapper.querySelector('.ottu__sdk-wallet-applied-value');
    if (appliedValueEl && billing.wallet_amount) {
      appliedValueEl.textContent = `-${billing.wallet_amount.display}`;
    }
  }

  _refreshSelectedMethodDisplay() {
    const billing = mainConfigService.get('billing') || null;
    const currencyCode = mainConfigService.get('currency_code') || '';
    const amount = mainConfigService.get('amount') || '';

    // When wallet has full coverage, selectePayMethod is guarded and would return
    // early — call changeTotal directly with the API-updated amounts instead.
    // Compose billing so the pay button shows the remainder (`amount`, e.g. KWD 0.000)
    // while .sdk-amount shows the wallet-covered amount via `original_amount` (e.g. KWD 100.000).
    if (this.isFullCoverage()) {
      const walletBilling = this.getWalletServiceBilling();
      const composedBilling = billing
        ? { ...billing, original_amount: walletBilling?.amount || billing.original_amount }
        : walletBilling;
      this.callbacks.changeTotal('', currencyCode, currencyCode, amount, '', composedBilling);
      return;
    }

    const selectedMethod = this.documentCheckout.querySelector(
      `.${elements.paymentMethod}.ottu__sdk-border-green`
    );
    const selectedCard = this.documentCheckout.querySelector(
      `.${elements.accordionItemRadio}:checked`
    );

    const element = selectedMethod || selectedCard;
    if (element) {
      this.callbacks.refreshSelectedMethodData(element);
    } else {
      this.callbacks.changeTotal('', currencyCode, currencyCode, amount, '', billing);
    }
  }

  _finalisePayButtonState() {
    // Full-coverage wallet: enabled regardless of PG selection
    if (this.isFullCoverage()) {
      this.callbacks.setPayButtonDisabled(false);
      return;
    }

    // Otherwise, only enable the pay button when a PG method or saved card is selected
    const selectedMethod = this.documentCheckout.querySelector(
      `.${elements.paymentMethod}.ottu__sdk-border-green`
    );
    const selectedCard = this.documentCheckout.querySelector(
      `.${elements.accordionItemRadio}:checked`
    );
    const hasSelection = !!(selectedMethod || selectedCard);

    this.callbacks.setPayButtonDisabled(!hasSelection);
  }
}

module.exports = WalletService;
